import { SegmentedControl, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { errorMessage, getCurrentUserInfoQueryKey, updateUserSettingsMutation } from "@/api";
import { Page } from "@/components/Page";
import { SettingsList, SettingsRow } from "@/components/section/SectionBlocks";
import { useTheme } from "@/theme/ThemeProvider";
import { THEME_PREFERENCES, type ThemePreference } from "@/theme/theme";

const ICONS: Record<ThemePreference, React.ReactNode> = {
  system: <Monitor size={14} />,
  light: <Sun size={14} />,
  dark: <Moon size={14} />,
};

/** Theme picker: applies instantly (local), then saves to the user's settings. */
export function AppearancePage() {
  const { preference, setPreference } = useTheme();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("settings");
  const options = THEME_PREFERENCES.map((value) => ({ value, label: t(`appearance.${value}`), icon: ICONS[value] }));
  const save = useMutation({
    ...updateUserSettingsMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getCurrentUserInfoQueryKey() }),
    onError: (error) => toast.error(errorMessage(error, t("appearance.saveFailed"))),
  });

  const choose = (next: ThemePreference) => {
    setPreference(next);
    save.mutate({ body: { theme: next } });
  };

  return (
    <Page title={t("appearance.title")} subtitle={t("appearance.subtitle")}>
      <SettingsList>
        <SettingsRow label={t("appearance.label")} description={t("appearance.description")}>
          <SegmentedControl aria-label={t("appearance.label")} options={options} value={preference} onChange={choose} />
        </SettingsRow>
      </SettingsList>
    </Page>
  );
}
